import dayjs from "dayjs";

export enum SortOrderType {
  TIME_TO_ENTER = "TIME_TO_ENTER",
  DESC = "DESC",
  ASC = "ASC",
}

export type EarnState = {
  poolsLongV1?: PoolLevel[] | null;
  poolsLongV2?: PoolLevel[] | null;
  closedPools: PoolLevel[] | null;
  endedPools: PoolLevel[] | null;
  poolsShort?: PoolLevel[] | null;
  firstLoaded: boolean;
  pageCount: number;
  sortOrder: SortOrderType;
  isLoadingFetchPools: boolean;
  isRefetchLoading: boolean;
};

export enum EarnAction {
  FETCH_POOLS_V1_START,
  FETCH_POOLS_V2_START,
  REFETCH_START,
  FETCH_POOLS_V1_SUCCESS,
  FETCH_POOLS_V2_SUCCESS,
  FETCH_POOLS_V1_FAILURE,
  FETCH_POOLS_V2_FAILURE,
  SORT_ORDER_CHANGE,
  MERGE_ENDED_POOLS,
}

type FetchPoolsStartPayloadType = {
  type: EarnAction.FETCH_POOLS_V1_START | EarnAction.FETCH_POOLS_V2_START;
};

type RefetchPoolsStartPayloadType = {
  type: EarnAction.REFETCH_START;
};

type FetchPoolsV2SuccessPayloadType = {
  type: EarnAction.FETCH_POOLS_V2_SUCCESS;
  payload: {
    poolsLongV2: PoolLevel[];
    poolsShort: PoolLevel[];
    closedPools: PoolLevel[];
    endedPools: PoolLevel[];
    pageCount: number;
  };
};

type FetchPoolsV1SuccessPayloadType = {
  type: EarnAction.FETCH_POOLS_V1_SUCCESS;
  payload: {
    poolsLongV1: PoolLevel[];
    closedPools: PoolLevel[];
    endedPools: PoolLevel[];
    pageCount: number;
  };
};

type FetchPoolsV2FailurePayloadType = {
  type: EarnAction.FETCH_POOLS_V2_FAILURE;
  payload: string;
};

type FetchPoolsV1FailurePayloadType = {
  type: EarnAction.FETCH_POOLS_V1_FAILURE;
  payload: string;
};

type SortOrderChangePayloadType = {
  type: EarnAction.SORT_ORDER_CHANGE;
  payload: SortOrderType;
};

type MergeEndedPoolsPayloadType = {
  type: EarnAction.MERGE_ENDED_POOLS;
};

export type EarnActionType =
  | FetchPoolsStartPayloadType
  | FetchPoolsV1SuccessPayloadType
  | FetchPoolsV2SuccessPayloadType
  | FetchPoolsV1FailurePayloadType
  | FetchPoolsV2FailurePayloadType
  | RefetchPoolsStartPayloadType
  | SortOrderChangePayloadType
  | MergeEndedPoolsPayloadType;

export const EarnReducer = (state: EarnState, action: EarnActionType) => {
  switch (action.type) {
    case EarnAction.FETCH_POOLS_V1_START: {
      return { ...state, isLoadingFetchPools: true };
    }
    case EarnAction.FETCH_POOLS_V2_START: {
      return { ...state, isLoadingFetchPools: true };
    }
    case EarnAction.REFETCH_START: {
      return { ...state, isRefetchLoading: true };
    }
    case EarnAction.FETCH_POOLS_V2_SUCCESS: {
      const newState = {
        ...state,
        isLoadingFetchPools: false,
        isRefetchLoading: false,
        poolsLongV2: action.payload.poolsLongV2,
        poolsShort: action.payload.poolsShort,
        pageCount: action.payload.pageCount,
        firstLoaded: false,
      };

      const v1ClosedPools =
        state.closedPools?.filter((pool) => pool.pool.version === "v1") || [];
      const v2ClosedPools = action.payload.closedPools;
      newState.closedPools = [...v1ClosedPools, ...v2ClosedPools];

      const v1EndedPools =
        state.endedPools?.filter((pool) => pool.pool.version === "v1") || [];
      const v2EndedPools = action.payload.endedPools;
      newState.endedPools = [...v1EndedPools, ...v2EndedPools];

      return newState;
    }
    case EarnAction.FETCH_POOLS_V1_SUCCESS: {
      const newState = {
        ...state,
        isLoadingFetchPools: false,
        poolsLongV1: action.payload.poolsLongV1,
        pageCount: action.payload.pageCount,
        firstLoaded: false,
      };

      const v2ClosedPools =
        state.closedPools?.filter((pool) => pool.pool.version === "v2") || [];
      const v1ClosedPools = action.payload.closedPools;
      newState.closedPools = [...v2ClosedPools, ...v1ClosedPools];

      const v2EndedPools =
        state.endedPools?.filter((pool) => pool.pool.version === "v2") || [];
      const v1EndedPools = action.payload.endedPools;
      newState.endedPools = [...v2EndedPools, ...v1EndedPools];

      return newState;
    }
    case EarnAction.FETCH_POOLS_V1_FAILURE: {
      return {
        ...state,
        isLoadingFetchPools: false,
      };
    }
    case EarnAction.FETCH_POOLS_V2_FAILURE: {
      return {
        ...state,
        isLoadingFetchPools: false,
        isRefetchLoading: false,
      };
    }
    case EarnAction.SORT_ORDER_CHANGE: {
      return {
        ...state,
        sortOrder: action.payload,
      };
    }
    case EarnAction.MERGE_ENDED_POOLS: {
      const v1EndedPools =
        state.endedPools?.filter((pool) => pool.pool.version === "v1") || [];
      const v2EndedPools =
        state.endedPools?.filter((pool) => pool.pool.version === "v2") || [];

      return {
        ...state,
        endedPools: [...v1EndedPools, ...v2EndedPools].sort((a, b) =>
          dayjs(b.pool.end || 0).diff(dayjs(a.pool.end || 0))
        ),
      };
    }
    default: {
      throw Error("Unknown action in Earn Reducer");
    }
  }
};

export const EarnInitState: EarnState = {
  poolsLongV1: null,
  poolsLongV2: null,
  poolsShort: null,
  closedPools: null,
  endedPools: null,
  isRefetchLoading: false,
  firstLoaded: true,
  pageCount: 0,
  sortOrder: SortOrderType.TIME_TO_ENTER,
  isLoadingFetchPools: false,
};
